// File: src/components/NotInEventRadiusBottomSheet.jsx
import React, { useEffect, useRef } from 'react';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { LuNavigation } from "react-icons/lu";

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

export default function NotInEventRadiusBottomSheet({ onNavigate, onDone }) {
  const scrollYRef = useRef(0);

  // Lock scroll + adjust theme color while visible (reliable “sheet fire” behavior)
  useEffect(() => {
    const themeMetaTag = document.querySelector('meta[name="theme-color"]');
    const body = document.body;

    scrollYRef.current = window.scrollY || window.pageYOffset || 0;
    body.style.position = 'fixed';
    body.style.top = `-${scrollYRef.current}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    document.documentElement.classList.add('overlay-active');
    document.getElementById('root')?.classList.add('overlay-active');
    if (themeMetaTag) themeMetaTag.content = '#0b0b0b';

    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, scrollYRef.current);

      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#ffffff';
    };
  }, []);

  return (
    <div style={S.backdrop} onClick={onDone}>
      <div
        style={{
          ...S.sheet,
          paddingBottom: `calc(12px + env(safe-area-inset-bottom, 0px) + ${isPWA() ? 8 : 0}px)`,
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Outside event radius"
      >
        {/* Header */}
        <div style={S.header}>
          <div style={S.headerTitle}>Heads up</div>
          <button
            aria-label="Close"
            style={S.headerCloseBtn}
            onClick={onDone}
          >
            <IoClose size={16} />
          </button>
        </div>
        <hr style={S.hairline} />

        {/* Hero map card (balanced margins; no right-side protrusion) */}
        <div style={S.heroCard}>
          <img src="/WhiteMap.jpeg" alt="Map" style={S.heroImg} />
          <div style={S.pinWrap}>
            <div style={S.pinCircle}>
              <FaMapMarkerAlt style={{ fontSize: 22, color: '#397AFF' }} />
            </div>
          </div>
        </div>

        {/* Copy */}
        <div style={S.copy}>
          <h2 style={S.title}>You’re outside the event radius</h2>
          <p style={S.subtitle}>Head to the event location to start matching.</p>
        </div>

        {/* Footer actions */}
        <div style={S.footer}>
  <button style={S.primaryBtn} onClick={onNavigate}>
    <LuNavigation
      aria-hidden
      style={{
        width: 16,
        height: 16,
        color: '#fff',     // matches primary button text color
        marginRight: 4,
        display: 'block',
        flexShrink: 0
      }}
    />
    <span>Take me there</span>
  </button>
  <button style={S.secondaryBtn} onClick={onDone}>
    Done
  </button>
</div>

        <style>{`
          @keyframes slideUp { from { transform: translateY(16px); opacity:0 } to { transform: translateY(0); opacity:1 } }
          @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important } }
          button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(59,130,246,.45) }
        `}</style>
      </div>
    </div>
  );
}

/* ───────────────────────── Styles (aligned with newer sheets) */
const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.50)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: 8,
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 520,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .25s ease-out',
    overflow: 'hidden',
  },
  header: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    padding: '0 12px',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
  },
  headerCloseBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: '1px solid #e5e7eb',
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'grid',
    placeItems: 'center', // perfect centering
    lineHeight: 0,        // prevents vertical bias
    color: '#0b0b0b',
    cursor: 'pointer',
  },
  hairline: {
    border: 'none',
    borderBottom: '1px solid #eef0f2',
    margin: 0,
  },

  // Map hero card with equal L/R margin so image never protrudes
  heroCard: {
    position: 'relative',
    width: 'calc(100% - 24px)',
    margin: '12px auto 0',
    aspectRatio: '16/10',
    minHeight: 180,
    background: '#fafafa',
    border: '1px solid #eef0f2',
    borderRadius: 10,
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  heroImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  pinWrap: {
    position: 'absolute',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    pointerEvents: 'none',
  },
  pinCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: '#fff',
    border: '1px solid #e5e7eb',
    display: 'grid',
    placeItems: 'center',
  },

  copy: {
    textAlign: 'center',
    padding: '14px 16px 0',
  },
  title: {
    margin: '0 0 6px',
    fontSize: 20,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
    lineHeight: 1.25,
  },
  subtitle: {
    margin: 0,
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 1.45,
  },

  footer: {
    padding: 12,
    display: 'flex',
    gap: 8,
    marginTop: 14,                 // keeps previous spacing from subtitle
    borderTop: '1px solid #eef0f2' // NEW line above button row
  },
  primaryBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    border: '1px solid #0b0b0b',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  secondaryBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#0b0b0b',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};
